import ApplicationController from "./application_controller"

// Persisted large-print step for public LexiQamus layouts (see lexiqamus.scss --lq-text-scale).
const LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_LOCAL_STORAGE_KEY = "lexiqamusTextScale"
const LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS = [0.875, 1, 1.125, 1.25, 1.5]
const LEXIQAMUS_ACCESSIBILITY_DEFAULT_TEXT_SCALE_MULTIPLIER = 1

// Snap stored/custom values to a toolbar step so UI state and CSS stay in sync.
function nearestAllowedTextScale(value) {
  const parsedTextScale = parseFloat(value)
  if (!Number.isFinite(parsedTextScale)) return LEXIQAMUS_ACCESSIBILITY_DEFAULT_TEXT_SCALE_MULTIPLIER
  return LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS.find(
    (multiplier) => Math.abs(multiplier - parsedTextScale) < 0.001
  ) || LEXIQAMUS_ACCESSIBILITY_DEFAULT_TEXT_SCALE_MULTIPLIER
}

// Floating A− / A / A+ toolbar (_accessibility_toolbar.html.erb) on lexiqamus_public_page and lexiqamus_results.
export default class extends ApplicationController {
  static targets = ["decreaseTextButton", "resetTextButton", "increaseTextButton"]

  connect() {
    this.textScale = LEXIQAMUS_ACCESSIBILITY_DEFAULT_TEXT_SCALE_MULTIPLIER
    this.restoreTextScale()
  }

  // Reapply saved scale on load without firing resize (layout is not dependent on scale yet).
  restoreTextScale() {
    let scale = LEXIQAMUS_ACCESSIBILITY_DEFAULT_TEXT_SCALE_MULTIPLIER
    try {
      scale = nearestAllowedTextScale(window.localStorage.getItem(LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_LOCAL_STORAGE_KEY))
    } catch (_e) {
      // ignore localStorage errors (private browsing, quota exceeded)
    }
    this.setLargePrintTextScale(scale, scale !== LEXIQAMUS_ACCESSIBILITY_DEFAULT_TEXT_SCALE_MULTIPLIER)
  }

  // Step down one entry in LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS (no-op at minimum).
  decreaseTextSize(event) {
    const currentMultiplierIndex = LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS.indexOf(this.textScale)
    if (currentMultiplierIndex > 0) {
      this.setLargePrintTextScale(LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS[currentMultiplierIndex - 1])
      this.announceFromControl(event?.currentTarget)
    }
  }

  // Step up one entry; indexOf guard covers a scale value that is not an exact step after restore.
  increaseTextSize(event) {
    const currentMultiplierIndex = LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS.indexOf(this.textScale)
    const lastMultiplierIndex = LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS.length - 1
    if (currentMultiplierIndex >= 0 && currentMultiplierIndex < lastMultiplierIndex) {
      this.setLargePrintTextScale(LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS[currentMultiplierIndex + 1])
      this.announceFromControl(event?.currentTarget)
    }
  }

  resetTextSize(event) {
    this.setLargePrintTextScale(LEXIQAMUS_ACCESSIBILITY_DEFAULT_TEXT_SCALE_MULTIPLIER)
    this.announceFromControl(event?.currentTarget)
  }

  // Single write path: CSS var drives rem scaling; persist choice; sync toolbar disabled/pressed state.
  setLargePrintTextScale(scale, notifyLayout = true) {
    this.textScale = nearestAllowedTextScale(scale)
    document.documentElement.style.setProperty("--lq-text-scale", String(this.textScale))
    this.updateTextSizeToolbarButtonStates()

    // Best-effort persistence — scaling still works if storage is unavailable.
    try {
      window.localStorage.setItem(LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_LOCAL_STORAGE_KEY, String(this.textScale))
    } catch (_e) {
      // ignore localStorage errors (private browsing, quota exceeded)
    }
    // TOC / scroll-spy and other width listeners recalc after rem scale changes.
    if (notifyLayout) window.dispatchEvent(new Event("resize"))
  }

  // WCAG 4.1.3 — copy from data-lexiqamus-accessibility-announce on the clicked toolbar button
  // into #lexiqamus-status-announcer (window.announceLexiqamusStatus).
  announceFromControl(control) {
    window.announceLexiqamusStatus?.(control?.dataset?.lexiqamusAccessibilityAnnounce)
  }

  // Disable A− / A+ at the ends of the scale so keyboard users do not activate no-op clicks.
  updateTextSizeToolbarButtonStates() {
    if (this.hasDecreaseTextButtonTarget) {
      this.decreaseTextButtonTarget.disabled = this.textScale <= LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS[0]
    }
    if (this.hasIncreaseTextButtonTarget) {
      const largestTextScaleMultiplier = LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS[
        LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS.length - 1
      ]
      this.increaseTextButtonTarget.disabled = this.textScale >= largestTextScaleMultiplier
    }
    if (this.hasResetTextButtonTarget) {
      // Reset is “selected” only at the default step (toggle-style control, not a separate mode).
      this.resetTextButtonTarget.setAttribute(
        "aria-pressed",
        this.textScale === LEXIQAMUS_ACCESSIBILITY_DEFAULT_TEXT_SCALE_MULTIPLIER ? "true" : "false"
      )
    }
  }
}

/* Accessibility: screen reader live region in LexiQamus layouts (#lexiqamus-status-announcer).
 * Announces transient status messages (e.g. copied text, section toggles, text scale changes). */
window.announceLexiqamusStatus = function(message) {
  if (!message) return;
  const announcer = document.getElementById('lexiqamus-status-announcer');
  if (!announcer) return;
  announcer.textContent = '';
  requestAnimationFrame(() => { announcer.textContent = message; });
}
